package ui.app.assign.detail.wheel.editor.pages

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.VerticalScrollbar
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollbarAdapter
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.drawIntoCanvas
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import api.ResourceOrientation
import apps.App
import ssoft.frontend.generated.resources.*
import tools.rememberString
import ui.app.assign.detail.definition.AppLogoField
import ui.app.assign.detail.wheel.AppWheelRendererGame
import ui.app.assign.detail.wheel.editor.AppEditorWheelScope
import ui.app.assign.detail.wheel.editor.widgets.FieldCustomImage
import ui.app.assign.detail.wheel.editor.widgets.FieldSlider
import ui.app.assign.detail.wheel.editor.widgets.FieldSwitch
import ui.app.assign.detail.wheel.editor.widgets.MobileDualView
import ui.widget.ColorField

private val itemsSingleColumn by lazy {
    listOf<@Composable AppEditorWheelScope.(AppEditorWheelScope.Wrapper) -> Unit>(
        { CardDefaultThemes(it) },
        { CardItemColors(it) },
        { CardBranding(it) },
        { CardBackgrounds(it) },
        { CardWheel(it) },
        { CardItemText(it) },
        { CardItemIcon(it) }
    )
}

private val itemsDoubleColumn by lazy {
    val left = listOf<@Composable AppEditorWheelScope.(AppEditorWheelScope.Wrapper) -> Unit>(
        { CardDefaultThemes(it) },
        { CardBackgrounds(it) },
    )

    val right = listOf<@Composable AppEditorWheelScope.(AppEditorWheelScope.Wrapper) -> Unit>(
        { CardItemColors(it) },
        { CardBranding(it) },
        { CardWheel(it) },
        { CardItemText(it) },
        { CardItemIcon(it) }
    )
    left to right
}

@Composable
fun AppEditorWheelScope.AppWheelPage02_Theme(nnvl: AppEditorWheelScope.Wrapper) {
    if (display.isMobileOrSmall) MobileView(nnvl)
    else DesktopView(nnvl)
}

@Composable
private fun AppEditorWheelScope.DesktopView(nnvl: AppEditorWheelScope.Wrapper) {
    Row(modifier = Modifier.fillMaxSize()) {
        PreviewTheme(nnvl, Modifier.weight(1F))
        ContentTheme(nnvl, Modifier.weight(1.5F))
    }
}

@Composable
private fun AppEditorWheelScope.MobileView(nnvl: AppEditorWheelScope.Wrapper) {
    MobileDualView(
        labelPrimary = Res.string.layer_bottom_wheel_tab_edit,
        labelSecondary = Res.string.layer_bottom_wheel_tab_preview,
        bodyPrimary = { ContentTheme(nnvl, Modifier.fillMaxSize()) },
        bodySecondary = { PreviewTheme(nnvl, Modifier.fillMaxSize()) }
    )
}

@Composable
private fun AppEditorWheelScope.ContentTheme(
    nnvl: AppEditorWheelScope.Wrapper,
    weight: Modifier
) {

    val state = rememberLazyListState()

    Box(weight.fillMaxHeight()) {
        LazyColumn(
            state = state,
            modifier = Modifier.fillMaxSize(),
            verticalArrangement = Arrangement.spacedBy(8.dp),
            contentPadding = PaddingValues(
                top = 16.dp,
                start = 16.dp,
                end = 16.dp,
                bottom = 64.dp
            )
        ) {
            if (display.isMobileOrSmall || display.resolution.width < 1300F) {
                item {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        itemsSingleColumn.forEach { layout ->
                            layout(nnvl)
                        }

                        Box(Modifier.fillMaxWidth()) {
                            ButtonNext(
                                modifier = Modifier.align(Alignment.Center),
                                validate = { true })
                        }
                    }
                }
            } else {
                item {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Column(
                                modifier = Modifier.weight(1F),
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                itemsDoubleColumn.first.forEach { layout ->
                                    layout(nnvl)
                                }
                            }
                            Column(
                                modifier = Modifier.weight(1F),
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                itemsDoubleColumn.second.forEach { layout ->
                                    layout(nnvl)
                                }
                            }
                        }
                        Box(Modifier.fillMaxWidth()) {
                            ButtonNext(
                                modifier = Modifier.align(Alignment.Center),
                                validate = { true })
                        }
                    }
                }
            }
        }
        VerticalScrollbar(
            modifier = Modifier.fillMaxHeight()
                .align(Alignment.CenterEnd)
                .padding(4.dp),
            adapter = rememberScrollbarAdapter(state)
        )
    }
}

@Composable
private fun AppEditorWheelScope.CardDefaultThemes(nnvl: AppEditorWheelScope.Wrapper) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.align(Alignment.CenterHorizontally)
                .padding(16.dp)
        ) {
            Text(
                text = Res.string.layer_bottom_wheel_page_theme_select.rememberString(),
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.align(Alignment.CenterHorizontally)
            )
            AppEditorWheelScope.ThemeWrapper.all.chunked(3).forEach { group ->
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    group.forEach { theme ->
                        with(theme[nnvl.orientation.state]) {
                            ThemeItem(
                                theme = this,
                                canEdit = nnvl.canEdit,
                                modifier = Modifier.weight(1F)
                            ) {
                                nnvl.applyTheme(this)
                            }
                        }
                    }
                }
            }
        }
    }
}


@Composable
private fun AppEditorWheelScope.CardBackgrounds(nnvl: AppEditorWheelScope.Wrapper) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.fillMaxWidth().padding(16.dp)
        ) {
            Text(
                text = Res.string.layer_bottom_wheel_page_theme_background_settings.rememberString(),
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.align(Alignment.CenterHorizontally),
                textAlign = TextAlign.Center
            )

            val inLand = remember {
                nnvl.orientation.state == App.Wheel.Theme.ORIENTATION_PORT
            }
            FieldCustomImage(
                companyId = args.companyId,
                entryId = args.appId,
                canEdit = nnvl.canEdit,
                image = nnvl.themeBgGame,
                label = Res.string.layer_bottom_wheel_page_theme_app_bg_game,
                orientation = when (nnvl.orientation.state) {
                    App.Wheel.Theme.ORIENTATION_LAND -> ResourceOrientation.HORIZONTAL
                    App.Wheel.Theme.ORIENTATION_PORT -> ResourceOrientation.VERTICAL
                    else -> ResourceOrientation.UNDEFINED
                },
                size = if (inLand) 400.dp else 256.dp,
                fixWidth = !inLand,
                modifier = Modifier.align(Alignment.CenterHorizontally),
                showResetButton = true
            )
            ColorField(
                field = nnvl.themeBgColor,
                label = Res.string.layer_bottom_wheel_page_theme_app_bg_color,
                modifier = Modifier.width(if (inLand) 400.dp else 256.dp)
                    .align(Alignment.CenterHorizontally)
            )
        }
    }
}

@Composable
private fun AppEditorWheelScope.CardWheel(nnvl: AppEditorWheelScope.Wrapper) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.align(Alignment.CenterHorizontally)
                .padding(16.dp)
        ) {
            Text(
                text = Res.string.layer_bottom_wheel_page_theme_wheel_settings.rememberString(),
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center
            )

            FieldSlider(
                label = Res.string.layer_bottom_wheel_page_theme_wheel_settings_scale,
                field = nnvl.themeWheelScale,
                modifier = Modifier.fillMaxWidth()
            )
            FieldSlider(
                label = Res.string.layer_bottom_wheel_page_theme_wheel_settings_pos_x,
                field = nnvl.themeWheelPosX,
                modifier = Modifier.fillMaxWidth()
            )
            FieldSlider(
                label = Res.string.layer_bottom_wheel_page_theme_wheel_settings_pos_y,
                field = nnvl.themeWheelPosY,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun AppEditorWheelScope.CardItemText(nnvl: AppEditorWheelScope.Wrapper) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.align(Alignment.CenterHorizontally)
                .padding(16.dp)
        ) {
            Text(
                text = Res.string.layer_bottom_wheel_page_theme_items_text.rememberString(),
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center
            )

            FieldSlider(
                label = Res.string.layer_bottom_wheel_page_theme_items_text_size,
                field = nnvl.themeItemFontSize,
                modifier = Modifier.fillMaxWidth()
            )
            FieldSlider(
                label = Res.string.layer_bottom_wheel_page_theme_items_text_y,
                field = nnvl.themeItemNameY,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}


@Composable
private fun AppEditorWheelScope.CardItemIcon(nnvl: AppEditorWheelScope.Wrapper) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.align(Alignment.CenterHorizontally)
                .padding(16.dp)
        ) {
            Text(
                text = Res.string.layer_bottom_wheel_page_theme_items_icon.rememberString(),
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center
            )
            FieldSlider(
                label = Res.string.layer_bottom_wheel_page_theme_items_icon_size,
                field = nnvl.themeItemIconScale,
                modifier = Modifier.fillMaxWidth()
            )
            FieldSlider(
                label = Res.string.layer_bottom_wheel_page_theme_items_icon_y,
                field = nnvl.themeItemIconY,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun AppEditorWheelScope.CardItemColors(nnvl: AppEditorWheelScope.Wrapper) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.align(Alignment.CenterHorizontally)
                .padding(16.dp)
        ) {
            Text(
                text = Res.string.layer_bottom_wheel_page_theme_items_color.rememberString(),
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center
            )
            Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                ColorField(
                    field = nnvl.themeItemColorBg,
                    label = Res.string.layer_bottom_wheel_page_theme_items_color_bg,
                    modifier = Modifier.weight(1F)
                )
                ColorField(
                    field = nnvl.themeItemColorFg,
                    label = Res.string.layer_bottom_wheel_page_theme_items_color_fg,
                    modifier = Modifier.weight(1F)
                )
            }
        }
    }
}

@Composable
private fun AppEditorWheelScope.CardBranding(nnvl: AppEditorWheelScope.Wrapper) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.align(Alignment.CenterHorizontally)
                .padding(16.dp)
        ) {
            Text(
                text = Res.string.layer_bottom_wheel_page_theme_branding.rememberString(),
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center
            )

            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                AppLogoField(
                    canEdit = nnvl.canEdit && nnvl.themeBrandingImageShow.state,
                    appId = args.appId,
                    companyId = args.companyId,
                    nav = nav,
                    logo = nnvl.themeBrandingImage,
                    label = Res.string.layer_bottom_wheel_page_theme_branding_logo,
                    size = (120).dp,
                    modifier = Modifier.let {
                        if (nnvl.themeBrandingImageShow.state) it
                        else it.alpha(0.8F)
                    }
                )
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    FieldSwitch(
                        label = Res.string.layer_bottom_wheel_page_theme_branding_logo_show,
                        field = nnvl.themeBrandingImageShow,
                        modifier = Modifier.fillMaxWidth()
                    )
                    FieldSlider(
                        label = Res.string.layer_bottom_wheel_page_theme_branding_logo_size,
                        field = nnvl.themeBrandingImageScale,
                        canEdit = nnvl.themeBrandingImageShow.state,
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }

            FieldSlider(
                label = Res.string.layer_bottom_wheel_page_theme_branding_circle_size,
                field = nnvl.themeBrandingScaleCircle,
                modifier = Modifier.fillMaxWidth()
            )
            ColorField(
                field = nnvl.themeBrandingBackground,
                label = Res.string.layer_bottom_wheel_page_theme_branding_circle_color,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}


@Composable
private fun ThemeItem(
    theme: App.Wheel.Theme,
    canEdit: Boolean,
    modifier: Modifier,
    onClick: () -> Unit
) {
    Card(
        onClick = onClick,
        enabled = canEdit,
        modifier = modifier
    ) {
        Canvas(Modifier.fillMaxWidth().aspectRatio(1F)) {
            drawIntoCanvas {
                drawPath(
                    Path().apply {
                        moveTo(0F, 0F)
                        lineTo(size.width, 0F)
                        lineTo(0F, size.height)
                        close()
                    },
                    Color(theme.backgroundColor)
                )
                drawPath(
                    Path().apply {
                        moveTo(size.width, 0F)
                        lineTo(size.width, size.height)
                        lineTo(center.x, center.y)
                        close()
                    },
                    Color(theme.items.surface)
                )
                drawPath(
                    Path().apply {
                        moveTo(0F, size.height)
                        lineTo(size.width, size.height)
                        lineTo(center.x, center.y)
                        close()
                    },
                    Color(theme.items.onSurface)
                )
            }
        }
    }
}


@Composable
private fun AppEditorWheelScope.PreviewTheme(nnvl: AppEditorWheelScope.Wrapper, modifier: Modifier) {
    Box(
        modifier.fillMaxHeight()
            .background(
                color = MaterialTheme.colorScheme.surfaceContainer,
                shape = RoundedCornerShape(
                    topEnd = 8.dp,
                    bottomEnd = 8.dp
                )
            )
    ) {
        AppWheelRendererGame(nnvl, Modifier.align(Alignment.Center))
    }
}


